import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Book, Coins, Zap, ChevronRight, Play, Award, Users, MessageCircle, Store, Globe, FileText, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { DonationSection } from '@/components/DonationSection';

const Index = () => {
  useSeoMeta({
    title: 'Bitcoin Life Panamá - Diplomado en Bitcoin',
    description: 'Programa completo de educación en Bitcoin. Aprende desde conceptos básicos hasta desarrollo avanzado. Construyendo economía circular en Ciudad de Panamá.',
  });

  const stats = [
    { label: 'Módulos Completos', value: '60+' },
    { label: 'Comercios Orange-Pilled', value: '100+' },
    { label: 'Horas de Contenido', value: '40+' },
    { label: 'Certificación Profesional', value: '100%' }
  ];

  const quickLinks = [
    {
      title: 'Rutas de Aprendizaje',
      description: 'Cursos estructurados de principiante a experto',
      icon: Award,
      href: '/learning-paths',
      color: 'from-orange-500 to-amber-500',
      count: '3 Módulos'
    },
    {
      title: 'Temas Fundamentales',
      description: '9 módulos completos cubriendo cada aspecto de Bitcoin',
      icon: Book,
      href: '/topics',
      color: 'from-blue-500 to-cyan-500',
      count: '9 Módulos'
    },
    {
      title: 'Lecciones Destacadas',
      description: 'Lecciones populares para comenzar tu viaje',
      icon: Zap,
      href: '/featured-lessons',
      color: 'from-purple-500 to-pink-500',
      count: '6 Lecciones'
    },
    {
      title: 'Qué es Nostr',
      description: 'Aprende sobre el protocolo que usamos',
      icon: Globe,
      href: '/#nostr',
      color: 'from-indigo-500 to-blue-500',
      count: 'Guía Completa'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-amber-50 to-yellow-100 dark:from-orange-950/30 dark:via-amber-950/20 dark:to-yellow-950/30 -z-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTEsMTkxLDM2LDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>
        </div>

        <div className="container mx-auto px-4 pt-20 pb-24">
          <div className="max-w-5xl mx-auto">
            {/* Hero Content */}
            <div className="text-center space-y-8 mb-16">
              <Badge className="px-4 py-1.5 text-sm bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300 border-orange-200 dark:border-orange-800">
                <Zap className="w-3.5 h-3.5 mr-1.5 inline" />
                Diplomado Profesional en Bitcoin
              </Badge>

              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-gray-900 via-orange-600 to-amber-600 dark:from-white dark:via-orange-400 dark:to-amber-400 bg-clip-text text-transparent">
                  Domina Bitcoin
                </span>
                <br />
                <span className="text-gray-700 dark:text-gray-300">
                  Construyendo Economía Circular
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
                Programa completo de Bitcoin Life Panamá. Aprende desde fundamentos hasta desarrollo avanzado,
                mientras contribuyes a la adopción de Bitcoin en Ciudad de Panamá y Latinoamérica.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
                <Link to="/lessons/beginner">
                  <Button size="lg" className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-lg shadow-orange-500/30 px-8 h-14 text-lg group">
                    <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                    Comenzar Gratis
                    <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/topics">
                  <Button size="lg" variant="outline" className="h-14 px-8 text-lg border-2">
                    <Book className="w-5 h-5 mr-2" />
                    Ver Currículo
                  </Button>
                </Link>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
              {stats.map((stat, index) => (
                <Card key={index} className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-orange-100 dark:border-orange-900/30 hover:shadow-lg transition-all duration-300 hover:scale-105">
                  <CardContent className="pt-6 text-center">
                    <div className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 dark:from-orange-400 dark:to-amber-400 bg-clip-text text-transparent mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Access Links */}
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              Explora Nuestro Contenido
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Accede rápidamente a los diferentes recursos educativos
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => {
              const Icon = link.icon;
              return (
                <Link key={index} to={link.href}>
                  <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border-2 hover:border-orange-200 dark:hover:border-orange-800 h-full">
                    <div className={`h-2 bg-gradient-to-r ${link.color}`}></div>
                    <CardHeader className="space-y-3">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${link.color} flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <CardTitle className="text-2xl group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                            {link.title}
                          </CardTitle>
                        </div>
                        <CardDescription className="text-base">
                          {link.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="text-xs">
                          {link.count}
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 group-hover:text-orange-600 transition-all" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Merchant Onboarding Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <Card className="overflow-hidden border-2 border-orange-200 dark:border-orange-800 bg-gradient-to-br from-white via-orange-50/30 to-amber-50/30 dark:from-gray-900 dark:via-orange-950/20 dark:to-amber-950/20">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left Column - Information */}
              <div className="p-8 md:p-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 mb-6 shadow-lg shadow-orange-500/30">
                  <Store className="w-8 h-8 text-white" />
                </div>

                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
                  ¿Eres Comerciante?
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                  Únete a nuestra red de comercios que aceptan Bitcoin en Ciudad de Panamá.
                  Ayúdanos a construir una economía circular próspera.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-950 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ChevronRight className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                        Visibilidad en Nuestra Plataforma
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Tu negocio será destacado en nuestro directorio de comercios Bitcoin-friendly
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-950 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ChevronRight className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                        Capacitación Gratuita
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Recibe soporte y educación sobre cómo aceptar pagos en Bitcoin
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-950 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ChevronRight className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                        Comunidad Activa
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Conecta con otros comerciantes y Bitcoiners en Panamá
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - CTA */}
              <div className="bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-600 p-8 md:p-12 flex flex-col justify-center items-center text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>

                <div className="relative z-10 space-y-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm">
                    <Store className="w-10 h-10 text-white" />
                  </div>

                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      Registra Tu Comercio
                    </h3>
                    <p className="text-lg text-white/90 mb-8">
                      Completa el formulario y comienza a aceptar Bitcoin hoy
                    </p>
                  </div>

                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSc_hpGtvk4_-KcZJcD8_mA7_fwH3mZmZ7vRT-PY0XAitz_WWw/viewform"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="lg"
                      className="bg-white text-orange-600 hover:bg-gray-100 shadow-xl h-14 px-10 text-lg font-semibold group"
                    >
                      <FileText className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                      Llenar Formulario
                      <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>

                  <p className="text-sm text-white/80">
                    ¡Es gratis y toma solo 2 minutos!
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <DonationSection />
      <Footer />
    </div>
  );
};

export default Index;
